import LegalPage from "@/components/legal/LegalPage";

// Shared section-heading styles for this legal page. Kept inline (not in
// CSS) so the shell in LegalPage.jsx remains untouched per instruction.
const H2 = { color: "#f7f7f7", fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 10, letterSpacing: "0.2px" };
const H3 = { color: "#f7f7f7", fontSize: 15, fontWeight: 700, marginTop: 20, marginBottom: 8, letterSpacing: "0.2px" };
const P  = { margin: "0 0 12px 0" };
const B  = { margin: "0 0 6px 0", paddingLeft: 4 };
const A  = { color: "#C9A86A", textDecoration: "underline" };

export default function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy" testid="legal-privacy">
      <h2 style={H2}>Introduction</h2>

      <p style={P}>
        Calbydoneit Limited (trading as CDI Collectables) (we, us, our) complies with the New Zealand Privacy Act 2020 (the Act) when dealing with personal information. Personal information is information about an identifiable individual (a natural person).
      </p>
      <p style={P}>
        This policy sets out how we will collect, use, disclose and protect your personal information.
      </p>
      <p style={P}>
        This policy does not limit or exclude any of your rights under the Act. If you wish to seek further information on the Act, see <a href="https://www.privacy.org.nz" target="_blank" style={A}>www.privacy.org.nz</a>.
      </p>

      <h3 style={H3}>Changes to this policy</h3>
      <p style={P}>
        We may change this policy by uploading a revised policy onto the website. The change will apply from the date that we upload the revised policy.
      </p>
      <p style={P}>
        This policy was last updated on 31 August 2026.
      </p>

      <h3 style={H3}>Who do we collect your personal information from</h3>
      <p style={P}>We collect personal information about you from:</p>
      <p style={B}>▲          you, when you provide that personal information to us, including via the website, through any registration or subscription process, through any contact with us (e.g. telephone call or email), when you access or use the CDI Collectibles web application (Service), or when you input or upload personal information into the Service</p>
      <p style={B}>▲          third parties where you have authorised this. This may include accessing certain personal information from third party authentication services (e.g. Google or Apple) if you login to your account with us using these services. The third party provider that we use will be displayed and you can visit its privacy policy for further details on how it deals with personal information</p>
      <p style={B}>▲          third parties where the information is publicly available.</p>
      <p style={P}>If possible, we will collect personal information from you directly.</p>

      <h3 style={H3}>How we use your personal information</h3>
      <p style={P}>We will use your personal information:</p>
      <p style={B}>▲          to verify your identity</p>
      <p style={B}>▲          to provide our website and the Service to you</p>
      <p style={B}>▲          to authenticate your access to the Service through third‑party login providers</p>
      <p style={B}>▲          to market the Service to you, including contacting you electronically (e.g. by text or email for this purpose)</p>
      <p style={B}>▲          to improve the Service that we provide to you</p>
      <p style={B}>▲          to bill you and to collect money that you owe us, including authorising and processing credit card transactions</p>
      <p style={B}>▲          to respond to communications from you, including a complaint</p>
      <p style={B}>▲          to conduct research and statistical analysis (on an anonymised basis)</p>
      <p style={B}>▲          to protect and/or enforce our legal rights and interests, including defending any claim</p>
      <p style={B}>▲          for any other purpose authorised by you or the Act.</p>
      <p style={P}>
        We use a third party service provider, Stripe, to process debit and credit card transactions. We do not have access to your debit or credit card information. You can see further information about how Stripe processes your debit/credit card information in its privacy policy at <a href="https://stripe.com/privacy" target="_blank" style={A}>stripe.com/privacy</a>.
      </p>
      <p style={P}>
        We do not use the data, content and information that you input or upload into the Service for any purpose other than providing the Service to you.
      </p>

      <h3 style={H3}>Disclosing your personal information</h3>
      <p style={P}>We may disclose your personal information to:</p>
      <p style={B}>▲          any business that supports our website or the Service, including any person that hosts or maintains any underlying IT system or data centre that we use to provide the website or the Service</p>
      <p style={B}>▲          professional advisers e.g. accountants, lawyers or auditors</p>
      <p style={B}>▲          a person who can require us to supply your personal information (e.g. a regulatory authority)</p>
      <p style={B}>▲          any other person authorised by the Act or another law (e.g. a law enforcement agency)</p>
      <p style={B}>▲          any other person authorised by you.</p>
      <p style={P}>
        A business that supports our website or the Service may be located outside New Zealand. This may mean your personal information is held and processed outside New Zealand.
      </p>
      <p style={P}>
        We may transfer your information in the case of a sale, merger, consolidation, liquidation, reorganisation or acquisition.
      </p>

      <h3 style={H3}>Protecting your personal information</h3>
      <p style={P}>
        We will take reasonable steps to keep your personal information safe from loss, unauthorised activity or other misuse.
      </p>
      <p style={P}>
        You play an important role in keeping your personal information secure by maintaining the confidentiality of any password and accounts used to access the Service. You should not disclose your password to third parties. Please notify us immediately if there is any unauthorised use of your account or any other breach of security.
      </p>

      <h3 style={H3}>Accessing and correcting your personal information</h3>
      <p style={P}>
        Subject to certain grounds for refusal set out in the Act, you have the right to access your readily retrievable personal information that we hold and to request a correction to your personal information. Before you exercise this right, we will need evidence to confirm that you are the individual to whom the personal information relates.
      </p>
      <p style={P}>
        In respect of a request for correction, if we think the correction is reasonable, and we are reasonably able to change the personal information, we will make the correction. If we do not make the correction, we will take reasonable steps to note on the personal information that you requested the correction.
      </p>
      <p style={P}>
        If you want to exercise either of the above rights, email us at <a href="mailto:support@cdicollectables.co.nz" style={A}>support@cdicollectables.co.nz</a>. Your email should provide evidence of who you are and set out the details of your request (e.g. the personal information, or the correction, that you are requesting).
      </p>
      <p style={P}>
        We may charge you our reasonable costs of providing to you copies of your personal information or correcting that information.
      </p>

      <h3 style={H3}>Internet use</h3>
      <p style={P}>
        While we take reasonable steps to maintain secure internet connections, if you provide us with personal information over the internet, the provision of that information is at your own risk.
      </p>
      <p style={P}>
        If you follow a link on our website or the Service to another site, the owner of that site will have its own privacy policy relating to your personal information. We suggest you review that site's privacy policy before you provide personal information.
      </p>

      <h3 style={H3}>COOKIES</h3>
      <p style={P}>
        We use cookies (an alphanumeric identifier that we transfer to your computer's hard drive so that we can recognise your browser) to monitor your use of the website and/or the Service. You may disable cookies by changing the settings on your browser, although this may mean that you cannot use all of the features of the website and/or the Service.
      </p>

      <h3 style={H3}>CONTACTING US</h3>
      <p style={P}>
        If you have any questions about this privacy policy or our privacy practices, or if you would like to request access to, or correction of, your personal information, you can contact us at <a href="mailto:support@cdicollectables.co.nz" style={A}>support@cdicollectables.co.nz</a>.
      </p>
    </LegalPage>
  );
}
